// Lazy catalog loader (Conv-30) — the fix for the +122 KB main-bundle regression.
//
// Conv-27/28 added seven locales and index.tsx imported all twelve catalog files
// STATICALLY, so every visitor downloaded all six non-English catalogs (~1.1 MB of
// source, +122 KB gzip) to render an app they were reading in English. Here each
// non-English locale becomes its own async chunk, requested only when it is the
// active locale.
//
// Three properties this file must keep, in order of how badly they bite:
//
// 1. FAIL OPEN. A catalog is a nicety; the English source string is the key, so a
//    catalog that never arrives degrades to English — exactly the graceful fallback
//    the gettext design already relies on. A rejected or hung import must therefore
//    resolve to an empty catalog, NEVER reject and never hang: the provider blocks
//    first paint on this promise, so a wedged import would be a blank app. Chunk
//    404s are a real event here — the deploy ships a fresh dist/ while old tabs hold
//    the old chunk hashes.
// 2. A FAILURE IS NOT CACHED. Only successes go in the cache, so a transient blip
//    does not strand the session in English forever; the next locale change retries.
// 3. NO EAGER PATH. Nothing outside this file may import a catalog — one static
//    import anywhere reachable from the entry re-bundles everything into the main
//    chunk and silently undoes this batch. `npm run check:i18n-bundle` asserts that
//    against the real build output, because nothing else can see it.

import type { Locale } from './locales';
import type { Catalog } from './catalogs/merge';

export type { Catalog };

// English IS the key — there is no lookup table, and this is the object `t` uses
// for it. Frozen so a stray write cannot turn the fallback into a cache.
const EMPTY: Catalog = Object.freeze({}) as Catalog;

type NonEnglish = Exclude<Locale, 'en-IN'>;

// One dynamic import per locale. The literal specifiers are deliberate: Rollup/Vite
// can only code-split on a statically analysable import(), so a computed
// `import('./catalogs/' + code)` would either fail to split or pull in every match.
const LOADERS: Record<NonEnglish, () => Promise<{ default: Catalog }>> = {
  'hi-IN': () => import('./catalogs/hi'),
  'bn-IN': () => import('./catalogs/bn'),
  'ta-IN': () => import('./catalogs/ta'),
  'te-IN': () => import('./catalogs/te'),
  'mr-IN': () => import('./catalogs/mr'),
  'kn-IN': () => import('./catalogs/kn'),
  'gu-IN': () => import('./catalogs/gu'),
  'pa-IN': () => import('./catalogs/pa'),
  'as-IN': () => import('./catalogs/as'),
  'ml-IN': () => import('./catalogs/ml'),
};

const cache = new Map<Locale, Catalog>();
const inflight = new Map<Locale, Promise<Catalog>>();

// Subscribers waiting for a catalog that arrives AFTER the timeout already resolved.
//
// The timeout resolves EMPTY so first paint is never held hostage, but the real import
// keeps going and usually lands a second or two later. Without this, that catalog was
// cached and then never used: the provider had already committed to English, nothing
// re-rendered, and the session stayed English with the correct translations sitting in
// memory — on exactly the slow connection the timeout exists to protect.
const lateSubscribers = new Map<Locale, Set<(catalog: Catalog) => void>>();

/** Notified only when a load resolves after its timeout already fired. */
export const onCatalogLate = (locale: Locale, fn: (catalog: Catalog) => void): (() => void) => {
  const set = lateSubscribers.get(locale) ?? new Set();
  set.add(fn);
  lateSubscribers.set(locale, set);
  return () => set.delete(fn);
};

// A hung import must not hold first paint hostage. Long enough that a slow 3G
// connection still gets its translations, short enough that a wedge is survivable.
const LOAD_TIMEOUT_MS = 8000;

/** The catalog for `locale` if it is already in memory, else null. Synchronous. */
export const cachedCatalog = (locale: Locale): Catalog | null =>
  locale === 'en-IN' ? EMPTY : cache.get(locale) ?? null;

/**
 * True when `locale`'s catalog is already in memory.
 *
 * NOT a readiness gate for rendering, and it must not be used as one. A FAILED load
 * is deliberately not cached (so it can be retried), so this stays false forever after
 * a chunk 404 — which is exactly how the first-paint gate once hung the app on a blank
 * splash while the loader believed it had failed open. The provider tracks its own
 * "resolved for this locale" state instead. Kept for callers that genuinely want to
 * know whether a fetch would be needed.
 */
export const isCatalogReady = (locale: Locale): boolean => cachedCatalog(locale) !== null;

/**
 * Load (or return the cached) catalog for `locale`. Never rejects — a failure
 * resolves to an empty catalog and the app renders English.
 */
export function loadCatalog(locale: Locale): Promise<Catalog> {
  const hit = cachedCatalog(locale);
  if (hit) return Promise.resolve(hit);

  const pending = inflight.get(locale);
  if (pending) return pending;

  const loader = LOADERS[locale as NonEnglish];
  if (!loader) return Promise.resolve(EMPTY); // unknown locale — English, same as normalizeLocale

  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<Catalog>((resolve) => {
    timer = setTimeout(() => {
      timedOut = true;
      resolve(EMPTY);
    }, LOAD_TIMEOUT_MS);
  });

  const load = loader()
    .then((mod) => {
      const catalog = mod.default ?? EMPTY;
      cache.set(locale, catalog); // only a SUCCESS is cached
      // Lost the race but still arrived: tell whoever already settled for English.
      if (timedOut) for (const fn of lateSubscribers.get(locale) ?? []) fn(catalog);
      return catalog;
    })
    .catch(() => {
      // Chunk 404 (stale tab after a deploy), offline, or a parse error. English is
      // a correct rendering, so this is a degradation and not an error to surface.
      console.warn(`[i18n] catalog for ${locale} could not be loaded — falling back to English.`);
      return EMPTY;
    });

  const race = Promise.race([load, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
    inflight.delete(locale); // cleared either way, so a failure can be retried
  });

  inflight.set(locale, race);
  return race;
}

// ── Document catalogs (2026-10-19) ───────────────────────────────────────────
// The legal documents (src/legal/*) and the Learn articles are long — about two
// thirds of all translated text by volume. Folded into the locale chunk above they
// made the Hindi chunk 2.7× bigger (85 → 232 KB gzip), and that chunk holds FIRST
// PAINT. So they live in their own per-locale file (src/i18n/messages.docs.<lang>.ts),
// fetched in the BACKGROUND once the app has painted — never on the first-paint path.
//
// Same three properties as the main loader, for the same reasons: it never rejects
// (a missing docs chunk means those pages read English, which is a correct
// rendering); only a success is cached; and nothing outside this file imports a docs
// catalog. A reader who deep-links straight to /privacy may see English for the
// moment it takes to arrive; the page then re-renders in place.
const DOC_LOADERS: Record<NonEnglish, () => Promise<Record<string, Catalog>>> = {
  'hi-IN': () => import('./messages.docs.hi'),
  'bn-IN': () => import('./messages.docs.bn'),
  'ta-IN': () => import('./messages.docs.ta'),
  'te-IN': () => import('./messages.docs.te'),
  'mr-IN': () => import('./messages.docs.mr'),
  'kn-IN': () => import('./messages.docs.kn'),
  'gu-IN': () => import('./messages.docs.gu'),
  'pa-IN': () => import('./messages.docs.pa'),
  'as-IN': () => import('./messages.docs.as'),
  'ml-IN': () => import('./messages.docs.ml'),
};

const docCache = new Map<Locale, Catalog>();
const docInflight = new Map<Locale, Promise<Catalog>>();

/** The docs catalog for `locale` if already in memory, else null. Synchronous. */
export const cachedDocs = (locale: Locale): Catalog | null =>
  locale === 'en-IN' ? EMPTY : docCache.get(locale) ?? null;

/** Load (or return the cached) docs catalog. Never rejects; a failure is not cached. */
export function loadDocs(locale: Locale): Promise<Catalog> {
  const hit = cachedDocs(locale);
  if (hit) return Promise.resolve(hit);
  const pending = docInflight.get(locale);
  if (pending) return pending;
  const loader = DOC_LOADERS[locale as NonEnglish];
  if (!loader) return Promise.resolve(EMPTY);
  const p = loader()
    .then((mod) => {
      // The module's only export is the catalog object; take it without naming it.
      const catalog = Object.values(mod)[0] ?? EMPTY;
      docCache.set(locale, catalog); // only a SUCCESS is cached
      return catalog;
    })
    .catch(() => {
      console.warn(`[i18n] document catalog for ${locale} could not be loaded — those pages stay English.`);
      return EMPTY;
    })
    .finally(() => docInflight.delete(locale));
  docInflight.set(locale, p);
  return p;
}

// ── Preload hint ──────────────────────────────────────────────────────────────
// The active locale is `localStorage override > account preferences.locale >
// English`, and the account preference is only known AFTER the session request
// lands. Waiting for it before starting the chunk would either flash English or
// stall the Landing page on an auth probe.
//
// So we remember the last locale that was actually resolved and use it ONLY to
// start the fetch early. It never decides the locale — precedence is unchanged —
// which is why it is a separate key from the override: writing the account
// preference into the override key would make a device silently ignore a later
// preference change made on another device.
const HINT_KEY = 'sansara.locale.hint';

export const readLocaleHint = (): string | null => {
  try {
    return localStorage.getItem(HINT_KEY);
  } catch {
    return null; // private mode / storage disabled — we just lose the head start
  }
};

export const writeLocaleHint = (locale: Locale): void => {
  try {
    localStorage.setItem(HINT_KEY, locale);
  } catch {
    /* storage unavailable — preloading is an optimisation, never a requirement */
  }
};
